/**
 * Status tones. A status is a colour plus an icon plus a word, never colour
 * alone. Neutral and success are quiet (icon in tone ink, no fill); info,
 * warning and danger are tinted (prototype spec, section 4.5).
 */
import {
  Alert02Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  CircleIcon,
  Clock01Icon,
  InformationCircleIcon,
} from '@hugeicons/core-free-icons';
import type { IconSvgObject } from '@/lib/icon-types';

export type Tone = 'neutral' | 'info' | 'warning' | 'danger' | 'success';

export interface ToneClasses {
  ink: string;
  tint: string;
  border: string;
  solid: string;
}

export const TONE_CLASSES: Record<Tone, ToneClasses> = {
  neutral: {
    ink: 'text-neutral-ink',
    tint: 'bg-neutral-tint',
    border: 'border-neutral-border',
    solid: 'bg-neutral-solid',
  },
  info: {
    ink: 'text-info-ink',
    tint: 'bg-info-tint',
    border: 'border-info-border',
    solid: 'bg-info-solid',
  },
  warning: {
    ink: 'text-warning-ink',
    tint: 'bg-warning-tint',
    border: 'border-warning-border',
    solid: 'bg-warning-solid',
  },
  danger: {
    ink: 'text-danger-ink',
    tint: 'bg-danger-tint',
    border: 'border-danger-border',
    solid: 'bg-danger-solid',
  },
  success: {
    ink: 'text-success-ink',
    tint: 'bg-success-tint',
    border: 'border-success-border',
    solid: 'bg-success-solid',
  },
};

export const TONE_ICON: Record<Tone, IconSvgObject> = {
  neutral: CircleIcon,
  info: InformationCircleIcon,
  warning: Clock01Icon,
  danger: CancelCircleIcon,
  success: CheckmarkCircle02Icon,
};

/** The icon an alert carries; a warning is a triangle, not a clock. */
export const TONE_ALERT_ICON: Record<Tone, IconSvgObject> = {
  ...TONE_ICON,
  warning: Alert02Icon,
};

export function isTinted(tone: Tone): boolean {
  return tone !== 'neutral' && tone !== 'success';
}

const GOOD = new Set([
  'posted',
  'paid',
  'credited',
  'ok',
  'match',
  'resolved',
  'closed',
  'active',
  'released',
  'refunded',
  'returned',
  'ready',
  'done',
  'delivered',
  'settled',
]);
const BAD = new Set([
  'failed',
  'mismatch',
  'break',
  'open',
  'review',
  'reversed',
  'refused',
  'error',
  'overdue',
  'none_landed',
  'not_matched',
]);
const WAITING = new Set([
  'pending',
  'held',
  'sending',
  'requested',
  'awaiting_approval',
  'in_progress',
  'waiting_customer',
  'waiting_provider',
  'building',
  'queued',
  'returning',
  'awaiting_statement',
  'not_settled',
  'not_completed',
]);

/** The tone a free-text server status reads as. */
export function toneForStatus(status: string): Tone {
  if (GOOD.has(status)) return 'success';
  if (BAD.has(status)) return 'danger';
  if (WAITING.has(status)) return 'warning';
  if (status === 'new') return 'info';
  return 'neutral';
}
